import { useId } from 'react'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import InfoHint from '../../components/InfoHint'
import { common as C } from './strings'
import './settings.css'

/**
 * The panel every settings form is built from. Each card posts on its own, as in
 * the legacy, so the save state belongs to the card rather than the page.
 *
 * The إلغاء / حفظ pair sits in a FOOTER, not the header the legacy puts it in:
 * an action reads after the fields it commits, not before them. It also lands
 * where the card grid needs it — cards stretch to the tallest in their row, and
 * a footer pushed to the bottom makes the action rows line up across the row
 * instead of floating at six different heights.
 *
 * `hint` renders the info glyph the legacy shows as a Bootstrap tooltip; `spec` is the
 * bare dimension string some image cards carry (`PNG (590 X 345)`).
 */
export default function SettingsCard({
  title,
  hint,
  spec,
  onSave,
  onReset,
  saving = false,
  saveLabel = C.save,
  actions,
  children,
}) {
  return (
    <section className="set-card">
      <header className="set-card__header">
        <h3 className="set-card__title">
          <bdi>{title}</bdi>
          {spec && (
            <span className="set-card__spec" dir="ltr">
              {spec}
            </span>
          )}
          {hint && <InfoHint content={<bdi>{hint}</bdi>} />}
        </h3>
      </header>

      <div className="set-card__body">{children}</div>

      {(actions || onReset || onSave) && (
        <footer className="set-card__actions">
          {actions}
          {onReset && (
            <Button variant="ghost" size="sm" onClick={onReset}>
              {C.reset}
            </Button>
          )}
          {onSave && (
            <Button size="sm" onClick={onSave} disabled={saving}>
              {saving ? '…' : saveLabel}
            </Button>
          )}
        </footer>
      )}
    </section>
  )
}

/** Label + control, with the info glyph a few of the legacy labels carry. */
export function Field({ label, hint, note, children }) {
  const id = useId()
  return (
    <div className="set-field">
      <label className="set-field__label" htmlFor={id}>
        <bdi>{label}</bdi>
        {note && (
          <small className="set-field__note">
            <bdi>{note}</bdi>
          </small>
        )}
        {hint && <InfoHint content={<bdi>{hint}</bdi>} />}
      </label>
      {typeof children === 'function' ? children(id) : children}
    </div>
  )
}

/** A checkbox row with the optional explanatory tooltip the video settings use. */
export function ToggleRow({ id, checked, onChange, label, hint }) {
  return (
    <div className="set-toggle">
      <Checkbox id={id} checked={checked} onChange={onChange}>
        <bdi>{label}</bdi>
      </Checkbox>
      {hint && <InfoHint content={<bdi>{hint}</bdi>} />}
    </div>
  )
}
